/**
 * Post Reactions Component
 *
 * Handles user reactions to posts with AJAX
 */

class PostReactions {
    constructor(options = {}) {
        this.ajaxUrl = options.ajaxUrl || window.ajaxurl || '/wp-admin/admin-ajax.php';
        this.nonce = options.nonce || '';
        this.postId = options.postId || 0;
        this.isLoading = false; // Prevent duplicate requests
        this.selectors = {
            container: '#post-reactions-' + this.postId,
            buttons: '.reaction-btn',
            count: '.reaction-count'
        };

        this.init();
    }

    init() {
        this.cacheElements();
        this.createErrorContainer();
        this.bindEvents();
        this.loadReactions();
    }

    cacheElements() {
        this.container = document.querySelector(this.selectors.container);
        this.buttons = document.querySelectorAll(this.selectors.buttons);
    }

    createErrorContainer() {
        if (!this.container) return;

        // Check if error container already exists
        let errorContainer = this.container.querySelector('.reaction-error-message');
        if (!errorContainer) {
            errorContainer = document.createElement('div');
            errorContainer.className = 'reaction-error-message text-red-500 text-sm mt-2 hidden';
            this.container.appendChild(errorContainer);
        }
        this.errorContainer = errorContainer;
    }

    bindEvents() {
        this.buttons.forEach(button => {
            button.addEventListener('click', (e) => this.handleReactionClick(e));
        });
    }

    async loadReactions() {
        try {
            const response = await this.ajaxRequest({
                action: 'get_post_reactions',
                post_id: this.postId
            });

            if (response.success) {
                this.updateCounts(response.data.counts);
                this.highlightUserReaction(response.data.user_reaction);
            }
        } catch (error) {
            // Silent fail - reactions will be initialized with 0 counts
        }
    }

    async handleReactionClick(event) {
        event.preventDefault();

        // Prevent duplicate requests
        if (this.isLoading) {
            return;
        }

        const button = event.currentTarget;
        const reaction = button.dataset.reaction;
        const postId = button.dataset.postId;

        // Add loading state
        this.isLoading = true;
        button.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none');

        try {
            const requestData = {
                action: 'save_post_reaction',
                post_id: postId,
                reaction: reaction,
                nonce: this.nonce
            };

            const response = await this.ajaxRequest(requestData);

            if (response.success) {
                this.updateCounts(response.data.counts);
                this.highlightUserReaction(response.data.user_reaction);
            } else {
                this.showError(response.data?.message || 'An error occurred');
            }
        } catch (error) {
            this.showError('An error occurred');
        } finally {
            // Remove loading state
            this.isLoading = false;
            button.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
        }
    }

    ajaxRequest(data) {
        const formData = new URLSearchParams({
            ...data,
            nonce: this.nonce
        });

        return fetch(this.ajaxUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData.toString()
        })
        .then(response => response.json());
    }

    updateCounts(counts) {
        if (!counts) return;

        Object.entries(counts).forEach(([reaction, count]) => {
            const buttons = document.querySelectorAll(`${this.selectors.buttons}[data-reaction="${reaction}"]`);
            buttons.forEach(btn => {
                const countSpan = btn.querySelector(this.selectors.count);
                if (countSpan) {
                    countSpan.textContent = count;
                }
            });
        });
    }

    highlightUserReaction(userReaction) {
        // Remove active state from all buttons
        this.buttons.forEach(btn => {
            btn.classList.remove('border-brand-primary-600', 'bg-brand-primary-50');
            btn.classList.add('border-gray-200');
        });

        // Add active state to user's reaction
        if (userReaction) {
            const userButtons = document.querySelectorAll(`${this.selectors.buttons}[data-reaction="${userReaction}"]`);
            userButtons.forEach(btn => {
                btn.classList.remove('border-gray-200');
                btn.classList.add('border-brand-primary-600', 'bg-brand-primary-50');
            });
        }
    }

    showError(message) {
        if (!this.errorContainer) return;

        // Remove any existing timeout
        if (this.errorTimeout) {
            clearTimeout(this.errorTimeout);
        }

        // Show error message
        this.errorContainer.textContent = message;
        this.errorContainer.classList.remove('hidden');

        // Hide after 5 seconds
        this.errorTimeout = setTimeout(() => {
            this.errorContainer.classList.add('hidden');
        }, 5000);
    }

    destroy() {
        this.buttons.forEach(button => {
            button.removeEventListener('click', this.handleReactionClick);
        });
    }
}

// Store instances to prevent duplicates
const postReactionsInstances = new Map();

// Initialize immediately or on DOM ready
const initPostReactions = () => {
    const reactionContainers = document.querySelectorAll('[id^="post-reactions-"]');
    reactionContainers.forEach(container => {
        // Extract post ID from container ID (handles both main and sidebar containers)
        // Main: post-reactions-123 -> 123
        // Sidebar: post-reactions-sidebar-123 -> 123
        const postId = container.id.replace(/^post-reactions-(?:sidebar-)?/, '');

        // Only create one instance per post ID
        if (!postReactionsInstances.has(postId)) {
            const instance = new PostReactions({
                postId: postId,
                nonce: postReactionsData.nonce || '',
                ajaxUrl: postReactionsData.ajaxUrl || ''
            });
            postReactionsInstances.set(postId, instance);
        }

        // Update container reference for the existing instance
        const instance = postReactionsInstances.get(postId);
        if (instance && instance.container !== container) {
            instance.cacheElements();
        }
    });
};

// Initialize if DOM is ready, otherwise wait for DOMContentLoaded
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPostReactions);
} else {
    initPostReactions();
}

export default PostReactions;
